import React, { forwardRef, memo, useImperativeHandle, useRef } from "react"
import styled from "styled-components"
import { getCssSizeValue, getHtmlFromRedactorField, setCssVars } from "@/utils/utils"
import Text from "@/components/common/Text"
import { useRouter } from "next/router"
import { ICONS, LOCALES } from "@/constants"
import CommonIcon from "@/components/common/Icon/Icon"
import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { useIsDesktop } from "@/hooks/useDevice"

const Icon = styled(CommonIcon)`
	position: absolute;
	width: 25%;
	z-index: -1;
	right: 5%;
	transition: transform var(--rgr-duration) var(--ease);
	transform: translateX(${({ $isDown }) => ($isDown ? "100%" : 0)});

	&.left {
		right: unset;
		left: 5%;
		rotate: 180deg;
	}
`

const Wrapper = styled.div`
	--opacity: 1;
	--scale: 1;
	--size: ${getCssSizeValue(100, BREAKPOINTS.MOBILE)};
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	width: var(--size);
	height: var(--size);
	color: white;
	top: 50%;
	left: 50%;
	translate: -50% -50%;
	transform-origin: center;
	z-index: 1;
	pointer-events: none;
	opacity: var(--opacity, 1);
	transition: opacity var(--rgr-duration) var(--ease);
	transform: translate3d(var(--x), var(--y), 0);

	.text {
		text-align: center;
		display: flex;
		flex-direction: column;

		span {
			font-weight: 400;
		}
	}

	&:before {
		content: " ";
		position: absolute;
		inset: 0;
		border-radius: 50%;
		background-color: ${({ theme }) => theme.colors.chilliRed};
		//box-shadow: 0 0 10px ${({ theme }) => theme.colors.chilliRed};
	}

	&:before,
	.text {
		transition: scale var(--rgr-duration) var(--ease);
		scale: var(--scale);
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		--size: ${getCssSizeValue(120)};
	}
`

const Arrow = ({ isDown, left }) => (
	<Icon name={ICONS.CHEVRON} className={`${left ? "left" : ""}`} color="antiflashWhite" $isDown={isDown} />
)

const TEXT = {
	PL: ["PRZESUŃ", "<span>lub</span>", "KLIKNIJ"],
	EN: ["SWIPE", "<span>or</span>", "CLICK"],
	DE: ["BEWEGE", "<span>oder</span>", "KLICKEN"],
}

const SwipeCursor = forwardRef(function SwipeCursor({ children, isDown = false, ...props }, fref) {
	const CursorRef = useRef()
	const { locale } = useRouter()
	const text = TEXT?.[locale.toUpperCase()] || LOCALES.PL
	const isDesktop = useIsDesktop()

	useImperativeHandle(fref, () => CursorRef.current, [])

	useIsomorphicLayoutEffect(() => {
		setCssVars(CursorRef.current, {
			scale: isDown ? 0.8 : 1,
			opacity: !isDesktop && isDown ? 0.5 : 1,
		})
	}, [isDown])

	return (
		<Wrapper ref={CursorRef} {...props}>
			<Text className="typo-borna-12" as="div" dangerouslySetInnerHTML={getHtmlFromRedactorField(text.join(""))} />
			<Arrow isDown={isDown} />
			<Arrow isDown={isDown} left />
			{children}
		</Wrapper>
	)
})

export default memo(SwipeCursor)
